import {
	type CompanyDetails,
	CompanyDetailsUpdate,
	MAX_LOGO_BYTES,
} from "@cleanbudget/contract/company-details";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import {
	IconAlertCircle,
	IconCheck,
	IconInfoCircle,
	IconLoader2,
	IconPhotoUp,
	IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useId, useRef, useState } from "react";
import { useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { Form, FormError, SubmitButton, TextField, useZodForm } from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

const detailsQuery = () => orpc.companyDetails.get.queryOptions();

/**
 * Bedrijfsgegevens: the company behind the shop, as every invoice states it, and the logo on it.
 * An invoice copies these when it is issued, so a change counts from the next invoice.
 */
export const Route = createFileRoute("/_admin/settings/company-details/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(detailsQuery()),
	component: CompanyDetailsPage,
});

function CompanyDetailsPage() {
	const { t } = useTranslation("companyDetails");
	const { t: tSettings } = useTranslation("settings");
	const details = useSuspenseQuery(detailsQuery());
	const [formKey, setFormKey] = useState(0);
	const reload = async () => {
		await details.refetch();
		setFormKey((key) => key + 1);
	};

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings">{tSettings("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
			/>
			{details.data.version === 0 ? (
				<Alert>
					<IconInfoCircle aria-hidden />
					<AlertDescription>{t("defaults")}</AlertDescription>
				</Alert>
			) : null}
			<LogoCard details={details.data} />
			<DetailsForm key={formKey} details={details.data} onReload={reload} />
		</div>
	);
}

const toValues = (details: CompanyDetails) => ({
	name: details.name,
	tradeName: details.tradeName ?? "",
	street: details.street,
	postcode: details.postcode,
	city: details.city,
	country: details.country,
	kvk: details.kvk,
	vatId: details.vatId,
	iban: details.iban,
	email: details.email ?? "",
	phone: details.phone ?? "",
	website: details.website ?? "",
	version: details.version,
});

/** Loads the details again after someone else saved them. */
// i18n-ignore: a type, not text
type Reload = () => PromiseLike<void>;

function DetailsForm({ details, onReload }: { details: CompanyDetails; onReload: Reload }) {
	const { t } = useTranslation("companyDetails");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const form = useZodForm(CompanyDetailsUpdate, { defaultValues: toValues(details) });
	const { isDirty } = useFormState({ control: form.control });
	const [saved, setSaved] = useState(false);
	const [changed, setChanged] = useState(false);
	const save = useMutation(orpc.companyDetails.update.mutationOptions());

	const submit = async (values: CompanyDetailsUpdate) => {
		setChanged(false);
		try {
			const result = await save.mutateAsync({ ...values, version: details.version });
			queryClient.setQueryData(detailsQuery().queryKey, result);
			form.reset(toValues(result));
			setSaved(true);
		} catch (error) {
			match(apiErrorTag(error))
				.with("CompanyDetailsChanged", () => {
					setChanged(true);
					form.setError("root", { type: "server", message: t("changed") });
				})
				.otherwise(() => form.setError("root", { type: "server", message: tCommon("form.failed") }));
		}
	};

	return (
		<Card>
			<CardContent>
				<Form form={form} onSubmit={submit}>
					<Section title={t("section.company")}>
						<TextField
							control={form.control}
							name="name"
							label={t("field.name")}
							autoComplete="organization"
						/>
						<TextField
							control={form.control}
							name="tradeName"
							label={t("field.tradeName")}
							description={t("field.tradeNameDescription")}
							autoComplete="off"
						/>
					</Section>
					<Section title={t("section.address")}>
						<TextField
							control={form.control}
							name="street"
							label={t("field.street")}
							autoComplete="street-address"
						/>
						<div className="grid gap-5 sm:grid-cols-[minmax(0,10rem)_1fr]">
							<TextField
								control={form.control}
								name="postcode"
								label={t("field.postcode")}
								autoComplete="postal-code"
							/>
							<TextField
								control={form.control}
								name="city"
								label={t("field.city")}
								autoComplete="address-level2"
							/>
						</div>
						<TextField
							control={form.control}
							name="country"
							label={t("field.country")}
							autoComplete="country-name"
						/>
					</Section>
					<Section title={t("section.registration")}>
						<div className="grid gap-5 sm:grid-cols-2">
							<TextField
								control={form.control}
								name="kvk"
								label={t("field.kvk")}
								description={t("field.kvkDescription")}
								inputMode="numeric"
								autoComplete="off"
							/>
							<TextField
								control={form.control}
								name="vatId"
								label={t("field.vatId")}
								description={t("field.vatIdDescription")}
								autoComplete="off"
							/>
						</div>
						<TextField
							control={form.control}
							name="iban"
							label={t("field.iban")}
							description={t("field.ibanDescription")}
							autoComplete="off"
						/>
					</Section>
					<Section title={t("section.contact")} description={t("section.contactDescription")}>
						<div className="grid gap-5 sm:grid-cols-2">
							<TextField
								control={form.control}
								name="email"
								type="email"
								label={t("field.email")}
								autoComplete="email"
							/>
							<TextField
								control={form.control}
								name="phone"
								type="tel"
								label={t("field.phone")}
								autoComplete="tel"
							/>
						</div>
						<TextField control={form.control} name="website" label={t("field.website")} autoComplete="url" />
					</Section>
					<FormError control={form.control} />
					{changed ? (
						<div>
							<Button type="button" variant="outline" onClick={onReload}>
								{t("reload")}
							</Button>
						</div>
					) : null}
					<div className="flex flex-wrap items-center gap-3">
						<SubmitButton control={form.control}>{t("save")}</SubmitButton>
						{saved && !isDirty ? (
							<output className="flex items-center gap-1 text-sm text-muted-foreground">
								<IconCheck className="size-4" aria-hidden />
								{tCommon("form.saved")}
							</output>
						) : null}
					</div>
				</Form>
			</CardContent>
		</Card>
	);
}

function Section({
	title,
	description,
	children,
}: {
	title: string;
	description?: string;
	children: ReactNode;
}) {
	return (
		<fieldset className="flex min-w-0 flex-col gap-5">
			<legend className="mb-1 font-heading text-base font-medium">{title}</legend>
			{description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
			{children}
		</fieldset>
	);
}

/** The types the API takes, for the file picker. */
const ACCEPT = "image/jpeg,image/png,image/webp,image/avif";

function LogoCard({ details }: { details: CompanyDetails }) {
	const { t } = useTranslation("companyDetails");
	const queryClient = useQueryClient();
	const inputId = useId();
	const input = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<string>();
	const onSaved = (result: CompanyDetails) => queryClient.setQueryData(detailsQuery().queryKey, result);
	const upload = useMutation({ ...orpc.companyDetails.uploadLogo.mutationOptions(), onSuccess: onSaved });
	const remove = useMutation({ ...orpc.companyDetails.deleteLogo.mutationOptions(), onSuccess: onSaved });

	const pick = async (file: File | undefined) => {
		if (input.current) input.current.value = "";
		if (file === undefined) return;
		setError(undefined);
		if (file.size > MAX_LOGO_BYTES) return setError(t("logo.tooLarge"));
		try {
			await upload.mutateAsync({ body: file });
		} catch (failure) {
			setError(
				match(apiErrorTag(failure))
					.with("NotAnImage", () => t("logo.notAnImage"))
					.with("LogoTooLarge", () => t("logo.tooLarge"))
					.otherwise(() => t("logo.failed")),
			);
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					<h2>{t("logo.title")}</h2>
				</CardTitle>
				<CardDescription>{t("logo.description")}</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center">
				<div className="flex h-24 w-full items-center justify-center rounded-xl bg-white p-3 ring-1 ring-foreground/10 sm:w-56">
					{details.logo ? (
						<img
							src={details.logo.url}
							alt={t("logo.alt")}
							className="max-h-full max-w-full object-contain"
						/>
					) : (
						<span className="text-sm text-neutral-500">{t("logo.none")}</span>
					)}
				</div>
				<div className="flex flex-col items-start gap-3">
					<input
						ref={input}
						id={inputId}
						type="file"
						accept={ACCEPT}
						className="sr-only"
						onChange={(event) => pick(event.target.files?.[0])}
					/>
					<div className="flex flex-wrap gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => input.current?.click()}
							disabled={upload.isPending}
						>
							{upload.isPending ? (
								<IconLoader2 className="animate-spin" aria-hidden />
							) : (
								<IconPhotoUp aria-hidden />
							)}
							{details.logo ? t("logo.replace") : t("logo.upload")}
						</Button>
						{details.logo ? (
							<Button
								type="button"
								variant="ghost"
								onClick={() => remove.mutate(undefined)}
								disabled={remove.isPending}
							>
								<IconTrash aria-hidden />
								{t("logo.remove")}
							</Button>
						) : null}
					</div>
					<p className="text-xs text-muted-foreground">{t("logo.hint")}</p>
					{error ? (
						<Alert variant="destructive">
							<IconAlertCircle aria-hidden />
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					) : null}
				</div>
			</CardContent>
		</Card>
	);
}
